import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Navigation, Clock, User, Phone, Package, Wifi, WifiOff, CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { useLiveTracking } from '@/hooks/useLiveTracking';
import { trackingService, OrderTrackingData } from '@/api/trackingService';
import { useAppSelector } from '@/redux/hooks';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import GlobalLoader from '@/components/ui/GlobalLoader';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface RouteWaypoint {
  _id?: string;
  lat: number;
  lng: number;
  address: string;
  order: number;
  type: 'pharmacy' | 'delivery' | 'other';
  pharmacy?: string;
  routeType?: string;
  completed?: boolean;
}

interface Route {
  _id: string;
  routeId: string;
  name: string;
  description: string;
  waypoints: RouteWaypoint[];
  assignedDriver: string;
  estimatedTime: number;
  distance: number;
  status: string;
  routeType: string;
  orders: string[];
  pickupOrder: string[];
  completedWaypoints: string[];
}

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  order?: {
    _id: string;
    orderNumber: string;
    customerName: string;
    customerPhone: string;
    pickupStatus: string;
    priority?: string;
    createdAt: string;
    pharmacyId: {
      _id: string;
      pharmacyName: string;
      location?: {
        latitude: number;
        longitude: number;
        address: string;
      };
    };
    riderInfo?: {
      id: string;
      name: string;
      phone: string;
      vehicleType: string;
      vehicleNumber: string;
      availability: string;
    };
    location?: {
      latitude: number;
      longitude: number;
      address: string;
    };
    routeId?: string;
  } | null;
  routeData?: Route;
  isRouteTracking?: boolean;
}

const OrderTrackingModal: React.FC<OrderTrackingModalProps> = ({ isOpen, onClose, order, routeData: propRouteData, isRouteTracking = false }) => {
  const [trackingDetails, setTrackingDetails] = useState<OrderTrackingData | null>(null);
  const [routeData, setRouteData] = useState<Route | null>(propRouteData || null);
  const [loading, setLoading] = useState(false);
  const [vehicleType, setVehicleType] = useState('bike');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [markers, setMarkers] = useState<L.Marker[]>([]);
  const [routePolylines, setRoutePolylines] = useState<L.Polyline[]>([]);

  const mapRef = useRef<HTMLDivElement>(null);
  const { trackingData: liveTrackingData, isConnected, subscribeToOrder, unsubscribeFromOrder, subscribeToRider, unsubscribeFromRider } = useLiveTracking();
  const { user } = useAppSelector((state) => state.auth);

  // Callback ref to avoid React ref warning
  const setMapRef = (node: HTMLDivElement | null) => {
    if (node) {
      mapRef.current = node;
    }
  };

  const getVehicleIcon = (type: string) => {
    const vehicleEmojis = {
      bike: '🏍️',
      car: '🚗',
      truck: '🚚',
      van: '🚐'
    };
    return vehicleEmojis[type as keyof typeof vehicleEmojis] || '🚗';
  };

  const createCustomIcon = (color: string, emoji: string, size: number = 32) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background-color: ${color};
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          border: 2px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: ${size * 0.5}px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">
          ${emoji}
        </div>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  };

  // Fetch tracking details when modal opens
  useEffect(() => {
    console.log('🔍 Modal useEffect:', { isOpen, isRouteTracking, propRouteData, order });
    if (isOpen) {
      if (isRouteTracking && propRouteData) {
        console.log('🔍 Setting route data:', propRouteData);
        setRouteData(propRouteData);
        setLoading(false);
      } else if (order) {
        fetchTrackingDetails();
      }
    }
  }, [isOpen, order, isRouteTracking, propRouteData]);

  // Subscribe to live tracking when order details are loaded
  useEffect(() => {
    console.log('🔗 Setting up live tracking subscription:', { 
      isOpen, 
      isRouteTracking, 
      riderId: routeData?.riderInfo?.id,
      orderId: order?._id 
    });
    
    if (isOpen) {
      if (isRouteTracking && routeData?.riderInfo?.id) {
        console.log('🔗 Subscribing to rider for route tracking:', routeData.riderInfo.id);
        subscribeToRider(routeData.riderInfo.id);
      } else if (order && trackingDetails) {
        console.log('🔗 Subscribing to order:', order._id);
        subscribeToOrder(order._id);
        
        if (order.riderInfo?.id) {
          console.log('🔗 Subscribing to rider for order tracking:', order.riderInfo.id);
          subscribeToRider(order.riderInfo.id);
        }
      }
    }
    
    return () => {
      if (isRouteTracking && routeData?.riderInfo?.id) {
        unsubscribeFromRider(routeData.riderInfo.id);
      } else if (order) {
        unsubscribeFromOrder(order._id);
        if (order.riderInfo?.id) {
          unsubscribeFromRider(order.riderInfo.id);
        }
      }
    };
  }, [isOpen, order, trackingDetails, isRouteTracking, routeData, subscribeToOrder, unsubscribeFromOrder, subscribeToRider, unsubscribeFromRider]);

  // Update map when live tracking data changes
  useEffect(() => {
    console.log('🔍 Live tracking data effect triggered:', { 
      hasLiveTrackingData: !!liveTrackingData, 
      hasMapInstance: !!mapInstance,
      liveTrackingData 
    });
    
    if (liveTrackingData && mapInstance) {
      console.log('🔍 Live tracking data received:', liveTrackingData);
      updateDriverMarker(liveTrackingData);
    }
  }, [liveTrackingData, mapInstance]);

  const fetchTrackingDetails = async () => {
    if (!order) return;
    
    setLoading(true);
    try {
      const response = await trackingService.getOrderTrackingDetails(order._id);
      if (response.success) {
        console.log('🔍 Tracking details received:', response.data);
        console.log('🔍 Tracking data:', response.data.trackingData);
        console.log('🔍 Route data:', response.data.routeData);
        console.log('🔍 Rider info:', response.data.order.riderInfo);
        setTrackingDetails(response.data);
        setRouteData(response.data.routeData || null);
        
        // Set map center based on tracking data or order location
        if (response.data.trackingData) {
          setMapCenter({
            lat: response.data.trackingData.latitude,
            lng: response.data.trackingData.longitude
          });
        } else if (order.location) {
          setMapCenter({
            lat: order.location.latitude,
            lng: order.location.longitude
          });
        } else if (order.pharmacyId?.location) {
          setMapCenter({
            lat: order.pharmacyId.location.latitude,
            lng: order.pharmacyId.location.longitude
          });
        }
      } else {
        toast.error(response.message || 'Failed to fetch tracking details');
      }
    } catch (error) {
      console.error('Error fetching tracking details:', error);
      toast.error('Failed to fetch tracking details');
    } finally {
      setLoading(false);
    }
  };

  // Function to draw route following roads using multiple approaches
  const drawRoadRoute = async (
    map: L.Map, 
    coordinates: [number, number][], 
    color: string, 
    polylinesArray: L.Polyline[], 
    weight: number = 4, 
    opacity: number = 0.7, 
    dashArray: string = '5, 5'
  ) => {
    console.log('🛣️ drawRoadRoute called:', { coordinates, color, weight, opacity, dashArray });
    try {
      // If only 2 points, try to get road route
      if (coordinates.length === 2) {
        const [start, end] = coordinates;
        
        // Try OpenRouteService first
        const apiKey = import.meta.env.VITE_OPENROUTE_API_KEY || '5b3ce3597851110001cf6248a8b8b8b8b8b8b8b8';
        
        console.log('🛣️ Requesting road route from OpenRouteService:', {
          start: [start[1], start[0]], // lng, lat format for API
          end: [end[1], end[0]], // lng, lat format for API
          apiKey: apiKey.substring(0, 10) + '...' // Hide full key
        });
        
        try {
          const response = await fetch(
            `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`,
            {
              method: 'GET',
              headers: {
                'Accept': 'application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8',
              }
            }
          );
          
          console.log('🛣️ OpenRouteService response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('🛣️ OpenRouteService response data:', data);
            
            if (data.features && data.features[0]?.geometry?.coordinates) {
              // Convert coordinates from [lng, lat] to [lat, lng] for Leaflet
              const routeCoordinates = data.features[0].geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]);
              
              console.log('🛣️ Drawing road-following route with', routeCoordinates.length, 'points');
              
              const routeLine = L.polyline(routeCoordinates, {
                color,
                weight,
                opacity,
                dashArray
              }).addTo(map);
              
              polylinesArray.push(routeLine);
              return;
            }
          } else {
            const errorText = await response.text();
            console.error('🛣️ OpenRouteService API error:', response.status, errorText);
          }
        } catch (apiError) {
          console.error('🛣️ OpenRouteService API request failed:', apiError);
        }
        
        // Fallback: Try OSRM (Open Source Routing Machine) - free, no API key needed
        try {
          console.log('🛣️ Trying OSRM routing service...');
          
          const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=geojson`;
          console.log('🛣️ OSRM URL:', osrmUrl);
          
          const osrmResponse = await fetch(osrmUrl);
          
          if (osrmResponse.ok) {
            const osrmData = await osrmResponse.json();
            console.log('🛣️ OSRM response:', osrmData);
            
            if (osrmData.routes && osrmData.routes[0]?.geometry?.coordinates) {
              // OSRM returns coordinates in [lng, lat] format
              const routeCoordinates = osrmData.routes[0].geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]);
              
              console.log('🛣️ Drawing OSRM road-following route with', routeCoordinates.length, 'points');
              
              const routeLine = L.polyline(routeCoordinates, {
                color,
                weight,
                opacity,
                dashArray
              }).addTo(map);
              
              polylinesArray.push(routeLine);
              return;
            }
          } else {
            const errorText = await osrmResponse.text();
            console.error('🛣️ OSRM API error:', osrmResponse.status, errorText);
          }
        } catch (osrmError) {
          console.error('🛣️ OSRM request failed:', osrmError);
        }
      }
      
      console.log('🛣️ Using straight line fallback');
      
      // Final fallback: straight line
      const routeLine = L.polyline(coordinates, {
        color,
        weight,
        opacity,
        dashArray
      }).addTo(map);
      
      polylinesArray.push(routeLine);
      
    } catch (error) {
      console.error('🛣️ All routing attempts failed, using straight line:', error);
      
      // Emergency fallback: straight line
      const routeLine = L.polyline(coordinates, {
        color,
        weight,
        opacity,
        dashArray
      }).addTo(map);
      
      polylinesArray.push(routeLine);
    }
  };

  const initializeMap = async () => {
    console.log('🗺️ Initializing map...', { mapRef: mapRef.current, routeData, isRouteTracking });
    if (!mapRef.current) {
      console.log('❌ Map ref not available');
      return;
    }

    // Clear existing map
    if (mapInstance) {
      mapInstance.remove();
    }

    // Clear existing markers and polylines
    markers.forEach(marker => marker.remove());
    routePolylines.forEach(polyline => mapInstance?.removeLayer(polyline));
    setMarkers([]);
    setRoutePolylines([]);

    // Determine map center
    let centerLat = 0;
    let centerLng = 0;

    if (mapCenter) {
      centerLat = mapCenter.lat;
      centerLng = mapCenter.lng;
    } else if (isRouteTracking && routeData?.trackingData) {
      // For route tracking, use rider's current location
      centerLat = routeData.trackingData.latitude;
      centerLng = routeData.trackingData.longitude;
    } else if (isRouteTracking && routeData?.waypoints && routeData.waypoints.length > 0) {
      // For route tracking, use first waypoint if no rider location
      centerLat = routeData.waypoints[0].lat;
      centerLng = routeData.waypoints[0].lng;
      console.log('🗺️ Using waypoint for map center:', { centerLat, centerLng });
    } else if (order?.location) {
      centerLat = order.location.latitude;
      centerLng = order.location.longitude;
    } else if (order?.pharmacyId?.location) {
      centerLat = order.pharmacyId.location.latitude;
      centerLng = order.pharmacyId.location.longitude;
    } else {
      // Default to a generic location if no coordinates available
      centerLat = 40.7128;
      centerLng = -74.0060;
    }

    console.log('🗺️ Map center coordinates:', { centerLat, centerLng });
    
    // Initialize new map
    console.log('🗺️ Creating map with center:', { centerLat, centerLng });
    const map = L.map(mapRef.current).setView([centerLat, centerLng], 13);

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    console.log('🗺️ Map created successfully:', map);

    const newMarkers: L.Marker[] = [];
    const newPolylines: L.Polyline[] = [];

    // Add route waypoints if route data is available
    if (routeData?.waypoints && routeData.waypoints.length > 0) {
      console.log('🔍 Drawing route polylines for waypoints:', routeData.waypoints);
      const waypointCoords: [number, number][] = [];
      
      routeData.waypoints.forEach((waypoint, index) => {
        const isCompleted = routeData.completedWaypoints?.includes(waypoint._id?.toString() || index.toString());
        
        // Privacy check: For pharmacy users, only show their own pharmacy and delivery points
        const isPharmacyUser = user?.role === 'pharmacy';
        const isAdminUser = user?.role === 'admin';
        const isOwnPharmacy = isPharmacyUser && waypoint.type === 'pharmacy' && 
          waypoint.pharmacy === user?.id;
        const isOwnDelivery = isPharmacyUser && waypoint.type === 'delivery';
        
        // Skip waypoints that pharmacy shouldn't see (admin can see all)
        if (isPharmacyUser && !isOwnPharmacy && !isOwnDelivery) {
          return;
        }
        
        // Add waypoint marker
        let markerColor = '#3B82F6'; // Blue for pending
        let emoji = '📍';
        
        if (waypoint.type === 'pharmacy') {
          markerColor = '#3B82F6';
          emoji = '🏥';
        } else if (waypoint.type === 'delivery') {
          markerColor = '#EF4444';
          emoji = '🏠';
        }
        
        if (isCompleted) {
          markerColor = '#10B981'; // Green for completed
          emoji = '✅';
        }

        const waypointMarker = L.marker(
          [waypoint.lat, waypoint.lng],
          { icon: createCustomIcon(markerColor, emoji) }
        ).addTo(map);

        waypointMarker.bindPopup(`
          <div class="p-2">
            <h3 class="font-semibold ${isCompleted ? 'text-green-600' : 'text-gray-700'}">
              ${waypoint.type === 'pharmacy' ? 'Pharmacy' : 'Delivery'} Point ${index + 1}
              ${isCompleted ? ' ✅' : ''}
            </h3>
            <p class="text-sm text-gray-600">${waypoint.address}</p>
            <p class="text-xs text-gray-500">Order: ${waypoint.order}</p>
          </div>
        `);

        newMarkers.push(waypointMarker);
        waypointCoords.push([waypoint.lat, waypoint.lng]);
      });

      // Draw route line connecting all waypoints (following roads)
      if (waypointCoords.length > 1) {
        console.log('🔍 Drawing route line with waypoints:', waypointCoords);
        await drawRoadRoute(map, waypointCoords, '#3B82F6', newPolylines);
      }
    }

    // Add pharmacy marker (if not in route waypoints)
    if (order?.pharmacyId?.location && !routeData?.waypoints?.some(wp => wp.type === 'pharmacy')) {
      const pharmacyMarker = L.marker(
        [order.pharmacyId.location.latitude, order.pharmacyId.location.longitude],
        { icon: createCustomIcon('#3B82F6', '🏥') }
      ).addTo(map);

      pharmacyMarker.bindPopup(`
        <div class="p-2">
          <h3 class="font-semibold text-blue-600">${order.pharmacyId.pharmacyName}</h3>
          <p class="text-sm text-gray-600">Pickup Location</p>
          <p class="text-xs text-gray-500">${order.pharmacyId.location.address}</p>
        </div>
      `);

      newMarkers.push(pharmacyMarker);
    }

    // Add delivery location marker (if not in route waypoints)
    if (order?.location && !routeData?.waypoints?.some(wp => wp.type === 'delivery')) {
      const deliveryMarker = L.marker(
        [order.location.latitude, order.location.longitude],
        { icon: createCustomIcon('#EF4444', '🏠') }
      ).addTo(map);

      deliveryMarker.bindPopup(`
        <div class="p-2">
          <h3 class="font-semibold text-red-600">${order.customerName}</h3>
          <p class="text-sm text-gray-600">Delivery Location</p>
          <p class="text-xs text-gray-500">${order.location.address}</p>
        </div>
      `);

      newMarkers.push(deliveryMarker);
    }

    // Add driver marker if tracking data is available and rider is online
    const currentTrackingData = isRouteTracking ? routeData?.trackingData : trackingDetails?.trackingData;
    const currentRiderInfo = isRouteTracking ? routeData?.riderInfo : order?.riderInfo;
    
    console.log('🚗 Checking for rider marker:', { 
      currentTrackingData, 
      currentRiderInfo, 
      isRouteTracking,
      hasTrackingData: !!currentTrackingData,
      isOnline: currentTrackingData?.isOnline 
    });
    
    if (currentTrackingData && currentTrackingData.isOnline) {
      const VehicleIcon = getVehicleIcon(vehicleType);
      const driverMarker = L.marker(
        [currentTrackingData.latitude, currentTrackingData.longitude],
        { icon: createCustomIcon('#10B981', VehicleIcon, 36) }
      ).addTo(map);

      driverMarker.bindPopup(`
        <div class="p-2">
          <h3 class="font-semibold text-green-600">${currentRiderInfo?.name}</h3>
          <p class="text-sm text-gray-600">${currentRiderInfo?.vehicleType} - ${currentRiderInfo?.vehicleNumber}</p>
          <p class="text-xs text-gray-500">Speed: ${currentTrackingData.speed} km/h</p>
          <p class="text-xs text-gray-500">Last updated: ${new Date(currentTrackingData.lastUpdated).toLocaleTimeString()}</p>
        </div>
      `);

      newMarkers.push(driverMarker);
      console.log('🚗 Rider marker added to map');

      // Draw full route path through all waypoints (road-following)
      if (routeData?.waypoints && routeData.waypoints.length > 0) {
        console.log('🛣️ Drawing road-following route through all waypoints (live data)');
        
        // Start from rider's current location
        let currentLocation = [currentTrackingData.latitude, currentTrackingData.longitude];
        
        // Draw road-following route to each waypoint in sequence
        for (let i = 0; i < routeData.waypoints.length; i++) {
          const waypoint = routeData.waypoints[i];
          const nextLocation = [waypoint.lat, waypoint.lng];
          
          console.log(`🛣️ Drawing road route from ${currentLocation} to ${nextLocation}`);
          
          // Draw road-following route between current location and next waypoint
          await drawRoadRoute(map, [currentLocation, nextLocation] as [number, number][], '#10B981', newPolylines, 3, 0.8, '10, 10');
          
          // Update current location to this waypoint for next iteration
          currentLocation = nextLocation;
        }
      }
    } else if (isRouteTracking && routeData?.waypoints && routeData.waypoints.length > 0) {
      // Fallback: Show rider at first waypoint if no tracking data
      console.log('🚗 No tracking data, showing rider at first waypoint');
      const firstWaypoint = routeData.waypoints[0];
      const VehicleIcon = getVehicleIcon(vehicleType);
      const driverMarker = L.marker(
        [firstWaypoint.lat, firstWaypoint.lng],
        { icon: createCustomIcon('#10B981', VehicleIcon, 36) }
      ).addTo(map);

      driverMarker.bindPopup(`
        <div class="p-2">
          <h3 class="font-semibold text-green-600">${currentRiderInfo?.name || 'Rider'}</h3>
          <p class="text-sm text-gray-600">Starting point</p>
          <p class="text-xs text-gray-500">No live location available</p>
        </div>
      `);

      newMarkers.push(driverMarker);
      console.log('🚗 Fallback rider marker added to map');

      // Draw full route path through all waypoints (road-following)
      if (routeData?.waypoints && routeData.waypoints.length > 0) {
        console.log('🛣️ Drawing road-following route through all waypoints (fallback)');
        
        // Start from first waypoint (rider's starting position)
        let currentLocation = [firstWaypoint.lat, firstWaypoint.lng];
        
        // Draw road-following route to each remaining waypoint in sequence
        for (let i = 0; i < routeData.waypoints.length; i++) {
          const waypoint = routeData.waypoints[i];
          const nextLocation = [waypoint.lat, waypoint.lng];
          
          console.log(`🛣️ Drawing road route from ${currentLocation} to ${nextLocation} (fallback)`);
          
          // Draw road-following route between current location and next waypoint
          await drawRoadRoute(map, [currentLocation, nextLocation] as [number, number][], '#10B981', newPolylines, 3, 0.8, '10, 10');
          
          // Update current location to this waypoint for next iteration
          currentLocation = nextLocation;
        }
      }
    }

    setMapInstance(map);
    setMarkers(newMarkers);
    setRoutePolylines(newPolylines);
  };

  const updateDriverMarker = (newTrackingData: any) => {
    if (!mapInstance || !order?.riderInfo) return;

    // Remove existing driver marker
    const existingDriverMarker = markers.find(marker => 
      marker.getLatLng().lat === trackingDetails?.trackingData?.latitude &&
      marker.getLatLng().lng === trackingDetails?.trackingData?.longitude
    );
    
    if (existingDriverMarker) {
      mapInstance.removeLayer(existingDriverMarker);
    }

    // Remove existing driver-to-waypoint lines
    routePolylines.forEach(polyline => {
      if (polyline.options.dashArray === '10, 10') {
        mapInstance.removeLayer(polyline);
      }
    });

    // Add new driver marker
    const VehicleIcon = getVehicleIcon(vehicleType);
    const driverMarker = L.marker(
      [newTrackingData.latitude, newTrackingData.longitude],
      { icon: createCustomIcon('#10B981', VehicleIcon, 36) }
    ).addTo(mapInstance);

    driverMarker.bindPopup(`
      <div class="p-2">
        <h3 class="font-semibold text-green-600">${order.riderInfo.name}</h3>
        <p class="text-sm text-gray-600">${order.riderInfo.vehicleType} - ${order.riderInfo.vehicleNumber}</p>
        <p class="text-xs text-gray-500">Speed: ${newTrackingData.speed} km/h</p>
        <p class="text-xs text-gray-500">Last updated: ${new Date(newTrackingData.lastUpdated).toLocaleTimeString()}</p>
      </div>
    `);

    // Draw new line from driver to next waypoint
    if (routeData?.waypoints && routeData.waypoints.length > 0) {
      const nextWaypoint = routeData.waypoints.find(wp => 
        !routeData.completedWaypoints?.includes(wp._id?.toString() || '0')
      );
      
      if (nextWaypoint) {
        const driverToWaypoint = L.polyline([
          [newTrackingData.latitude, newTrackingData.longitude],
          [nextWaypoint.lat, nextWaypoint.lng]
        ], {
          color: '#10B981',
          weight: 3,
          opacity: 0.8,
          dashArray: '10, 10'
        }).addTo(mapInstance);

        const updatedPolylines = routePolylines.filter(polyline => polyline.options.dashArray !== '10, 10');
        updatedPolylines.push(driverToWaypoint);
        setRoutePolylines(updatedPolylines);
      }
    }

    // Update markers array
    const updatedMarkers = markers.filter(marker => marker !== existingDriverMarker);
    updatedMarkers.push(driverMarker);
    setMarkers(updatedMarkers);

    // Pan to new location
    mapInstance.setView([newTrackingData.latitude, newTrackingData.longitude], mapInstance.getZoom());
  };

  // Initialize map when modal opens
  useEffect(() => {
    console.log('🗺️ Map useEffect triggered:', { isOpen, mapRef: mapRef.current, routeData, isRouteTracking });
    if (isOpen) {
      // Small delay to ensure DOM is ready
      setTimeout(async () => {
        console.log('🗺️ Calling initializeMap...');
        await initializeMap();
      }, 200);
    }
  }, [isOpen, mapCenter, order, routeData, isRouteTracking]);

  // Cleanup map when modal closes
  useEffect(() => {
    if (!isOpen && mapInstance) {
      mapInstance.remove();
      setMapInstance(null);
    }
  }, [isOpen, mapInstance]);

  if (!order && !isRouteTracking) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <Navigation className="h-5 w-5" />
            {isRouteTracking ? `Live Tracking - ${routeData?.name}` : `Live Tracking - ${order?.orderNumber}`}
          </DialogTitle>
          <DialogDescription>
            {isRouteTracking ? 'Track the real-time location and route of your delivery route' : 'Track the real-time location and route of your order delivery'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Order Info */}
            <div className="lg:col-span-1 space-y-4">
              {/* Connection Status */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isConnected ? (
                        <Wifi className="h-4 w-4 text-green-600" />
                      ) : (
                        <WifiOff className="h-4 w-4 text-red-600" />
                      )}
                      <span className="text-sm font-medium">
                        {isConnected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Vehicle:</span>
                      <Select 
                        value={vehicleType} 
                        onValueChange={setVehicleType}
                        disabled={user?.role !== 'admin'}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bike">🏍️ Bike</SelectItem>
                          <SelectItem value="car">🚗 Car</SelectItem>
                          <SelectItem value="truck">🚚 Truck</SelectItem>
                          <SelectItem value="van">🚐 Van</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order/Route Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {isRouteTracking ? 'Route Information' : 'Order Information'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isRouteTracking ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Route Name:</span>
                        <span className="text-sm">{routeData?.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Route ID:</span>
                        <span className="text-sm">{routeData?.routeId}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Status:</span>
                        <Badge variant="outline" className="text-xs">
                          {routeData?.status}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Distance:</span>
                        <span className="text-sm">{routeData?.distance?.toFixed(1)} km</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">ETA:</span>
                        <span className="text-sm">{Math.round(routeData?.estimatedTime || 0)} min</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Orders:</span>
                        <span className="text-sm">{(routeData?.orders?.length || 0) + (routeData?.pickupOrder?.length || 0)}</span>
                      </div>
                      {routeData?.allOrdersCompleted && (
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Status:</span>
                          <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                            All Orders Completed
                          </Badge>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Order Number:</span>
                        <span className="text-sm">{order?.orderNumber}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Customer:</span>
                        <span className="text-sm">{order?.customerName}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Phone:</span>
                        <span className="text-sm">{order?.customerPhone}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Status:</span>
                        <Badge variant="outline">{order?.pickupStatus}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Created:</span>
                        <span className="text-sm">{new Date(order?.createdAt || '').toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Rider Information */}
              {((isRouteTracking && routeData?.riderInfo) || (!isRouteTracking && order?.riderInfo)) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Rider Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Name:</span>
                      <span className="text-sm">{isRouteTracking ? routeData?.riderInfo?.name : order?.riderInfo?.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Phone:</span>
                      <span className="text-sm">{isRouteTracking ? routeData?.riderInfo?.phone : order?.riderInfo?.phone}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Vehicle:</span>
                      <span className="text-sm">{isRouteTracking ? `${routeData?.riderInfo?.vehicleType} - ${routeData?.riderInfo?.vehicleNumber}` : `${order?.riderInfo?.vehicleType} - ${order?.riderInfo?.vehicleNumber}`}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Status:</span>
                      <Badge variant="outline" className={isRouteTracking ? (routeData?.trackingData?.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800') : (trackingDetails?.trackingData?.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                        {isRouteTracking ? (routeData?.trackingData?.isOnline ? 'Online' : 'Offline') : (trackingDetails?.trackingData?.isOnline ? 'Online' : 'Offline')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Route Information */}
              {routeData && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Route Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Route:</span>
                      <span className="text-sm">{routeData.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Distance:</span>
                      <span className="text-sm">{routeData.distance} km</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Est. Time:</span>
                      <span className="text-sm">{routeData.estimatedTime} min</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Waypoints:</span>
                      <span className="text-sm">
                        {routeData.completedWaypoints?.length || 0} / {routeData.waypoints?.length || 0} completed
                      </span>
                    </div>
                    {routeData.allOrdersCompleted && (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Status:</span>
                        <Badge variant="outline" className="bg-gray-100 text-gray-600">
                          All Orders Completed
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Tracking Data */}
              {((isRouteTracking && routeData?.trackingData) || (!isRouteTracking && trackingDetails?.trackingData)) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Live Tracking Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Last Updated:</span>
                      <span className="text-sm">{new Date(isRouteTracking ? routeData?.trackingData?.lastUpdated : trackingDetails?.trackingData?.lastUpdated).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Speed:</span>
                      <span className="text-sm">{isRouteTracking ? routeData?.trackingData?.speed : trackingDetails?.trackingData?.speed} km/h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Status:</span>
                      <Badge className={isRouteTracking ? (routeData?.trackingData?.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800') : (trackingDetails?.trackingData?.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                        {isRouteTracking ? (routeData?.trackingData?.isOnline ? 'Online' : 'Offline') : (trackingDetails?.trackingData?.isOnline ? 'Online' : 'Offline')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Map */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {isRouteTracking && routeData?.allOrdersCompleted ? 'Route Map (Completed)' : 'Live Tracking Map'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isRouteTracking && routeData?.allOrdersCompleted ? (
                    <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                      <div className="text-center">
                        <div className="text-6xl mb-4">✅</div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Route Completed</h3>
                        <p className="text-gray-600">All orders in this route have been completed.</p>
                      </div>
                    </div>
                  ) : (
                    <div 
                      ref={setMapRef} 
                      className="w-full h-96 rounded-lg border"
                      style={{ minHeight: '500px' }}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Route Legend */}
              {routeData && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">Route Legend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                        <span>Pending Waypoints</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                        <span>Completed Waypoints</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                        <span>Delivery Points</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-600 rounded-full"></div>
                        <span>Rider Location</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Global loading overlay */}
        <GlobalLoader 
          isLoading={loading}
          message="Loading tracking data..."
          subMessage="Please wait while we fetch order tracking information"
        />
      </DialogContent>
    </Dialog>
  );
};

export default OrderTrackingModal;